import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/common";

const EMPTY = { name: "", billing_cycle: "monthly", contact_name: "", email: "", phone: "", address: "", abn: "", payment_terms_days: 14, notes: "" };
const BIZ = [["business_name", "Business name"], ["business_abn", "ABN"], ["business_phone", "Phone"], ["business_email", "Email"], ["business_address", "Address"], ["bank_details", "Bank details for payment (shown on invoices)"]];

const CompanyRow = ({ c, update, remove }) => {
  const f = (k, props = {}) => <Input value={c[k] ?? ""} onChange={(e) => update({ ...c, [k]: e.target.value })} className="h-9" data-testid={`company-${k}-${c.name}`} {...props} />;
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid={`company-row-${c.name}`}>
      <div className="grid gap-2 sm:grid-cols-[1fr_140px_110px_auto]">
        {f("name", { placeholder: "Company name" })}
        <select value={c.billing_cycle} onChange={(e) => update({ ...c, billing_cycle: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid={`company-cycle-${c.name}`}>
          <option value="weekly">Invoice weekly</option><option value="monthly">Invoice monthly</option>
        </select>
        <div className="flex items-center gap-1 text-xs text-slate-500">Due in <Input type="number" min={0} max={90} value={c.payment_terms_days} onChange={(e) => update({ ...c, payment_terms_days: Number(e.target.value) })} className="h-9 w-16" data-testid={`company-terms-${c.name}`} /> days</div>
        <button onClick={remove} className="justify-self-end rounded p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600" data-testid={`company-remove-${c.name}`}><X className="h-4 w-4" /></button>
      </div>
      <div className="mt-2 grid gap-2 sm:grid-cols-4">
        {f("contact_name", { placeholder: "Contact person" })}{f("email", { placeholder: "Accounts email" })}{f("phone", { placeholder: "Phone" })}{f("abn", { placeholder: "ABN" })}
        {f("address", { placeholder: "Billing address", className: "h-9 sm:col-span-4" })}
      </div>
    </div>
  );
};

export const CompaniesEditor = ({ draft, setDraft }) => {
  const [name, setName] = useState("");
  const companies = draft.companies || [];
  const add = () => {
    const v = name.trim();
    if (!v || companies.some((c) => c.name.toLowerCase() === v.toLowerCase())) return;
    setDraft({ ...draft, companies: [...companies, { ...EMPTY, name: v }] });
    setName("");
  };
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
      <Panel title="Account companies (invoiced weekly / monthly)">
        <p className="mb-3 text-xs text-slate-500">Jobs booked with “Bill to company” set to one of these are collected into one invoice per billing period instead of being paid on the day.</p>
        <div className="space-y-2">
          {companies.length === 0 && <p className="text-sm text-slate-400">No companies yet.</p>}
          {companies.map((c, i) => <CompanyRow key={i} c={c} update={(v) => setDraft({ ...draft, companies: companies.map((x, j) => (j === i ? v : x)) })} remove={() => setDraft({ ...draft, companies: companies.filter((_, j) => j !== i) })} />)}
        </div>
        <div className="mt-3 flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add company, e.g. 313" className="h-9" data-testid="company-input" />
          <Button size="sm" variant="outline" onClick={add} data-testid="company-add"><Plus className="h-4 w-4" /> Add</Button>
        </div>
      </Panel>
      <Panel title="Your business details (on invoices)">
        <div className="space-y-2">
          {BIZ.map(([k, label]) => (
            <div key={k}><label className="text-xs font-medium text-slate-600">{label}</label>
              {k === "bank_details" || k === "business_address" ? <textarea rows={2} value={draft[k] || ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" data-testid={`field-${k}`} /> : <Input value={draft[k] || ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} className="mt-1 h-9" data-testid={`field-${k}`} />}
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
};
